import { DisplayMode } from 'src/components/helpers/content/contentInterfaces';
import keys from '../constants/hammerstoneConstantKeys';

import { ConfigMetadata } from './configMetadataInterfaces';
import { GetActivityInfoResponse } from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client/clients/hammerstoneexposedrestfulservicelambda';

export interface OnFailureAlarm extends Omit<GetActivityInfoResponse['onFailureAlarm'], 'active' | 'severity'> {
	active: boolean;
	severity: keyof typeof keys.Severity;
}

//Activity Interface from Hammerstone Exposed API
// https://w.amazon.com/bin/view/HammerstoneAPIUserGuideTemp/Documentations/APIs#HGetActivityInfoResponseModel
export default interface Activity<ActivityType extends keyof typeof keys.ActivityType = any>
	extends Omit<
		GetActivityInfoResponse,
		| 'configurationMetadata'
		| 'onFailureAlarm'
		| 'activityPriority'
		| 'activityStatusCode'
		| 'activityType'
		| 'configChanged'
		| 'djsLastExecutionStatus'
		| 'preconditionChanged'
		| 's3PreconRegion'
		| 'scheduleType'
	> {
	/** Replaces configurationMetadata with a strongly typed, indexable `config` */
	activityType: ActivityType;
	config: ConfigMetadata<ActivityType>;
	alarm: OnFailureAlarm;
	preconditions: { key: 's3Key' | 's3Prefix'; value: string; activityId?: number }[];
	activityPriority: keyof typeof keys.ActivityPriority;
	activityStatusCode: keyof typeof keys.StatusCode;
	djsLastExecutionStatus: keyof typeof keys.DjsExecutionStatus;
	s3PreconRegion: keyof typeof keys.Region;
	scheduleType: keyof typeof keys.ScheduleType;
	configChanged: boolean;
	preconditionChanged: boolean;
}

export interface transformItemType {
	name?: string;
	type: string;
	value: string;
}
export interface transformParamItem extends transformItemType {
	type: keyof typeof keys.TransformParamType;
}
export interface transformSQLItem extends transformItemType {
	type: keyof typeof keys.TransformSQLType;
}

export interface ActivityContainerProps {
	header: string;
	mode: DisplayMode;
	activityId: string;
	editAction?: keyof typeof keys.ActivityFormActions;
	diffActivityId?: string;
}

/**
 * Common props for the reusable field-group components extracted from the activity containers
 * (e.g. RemedyAlarmFields, RetryFields, ScheduleFields). These are the fields that the bulk-edit
 * flow re-uses, so callers outside the standard container layout (where there is no `header`)
 * pass this narrower shape with an optional `disabled` switch to gate inputs.
 */
export type ActivityFieldGroupProps = Omit<ActivityContainerProps, 'header'> & {
	disabled?: boolean;
};

export interface LoadTargetObj {
	cluster: string;
	schema: string;
	db: string;
	table: string;
}

export interface ButtonDropdownItem {
	text: string;
	id: string;
	disabled?: boolean;
	disabledReason?: string;
}

/**
 * Creates a unique resourceId for an activity's revision by joining the activityId::revision
 * NOTE: this should be used to retrieve a revision from the `activity` Redux data slice
 * */
export function activityRevisionIdentifier(id: { activityId: number; revision: number }) {
	return `${id.activityId}::${id.revision}`;
}
